import React, { useEffect, useState, useRef } from "react";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import { useNavigate, useLocation } from "react-router";
import { createInquiry } from "../../../../api/action/inquiry";
import { INSTITUTION_ASSETS } from "../../../s3-assets/Institution";
import BackButton from "../components/CustomFields/BackButton";
import { useSelector } from "react-redux";
import InstitutionHeader from "../components/InstitutionHeader";
import BackgroundComponent from "../components/theme/BackgroundAnimation";
import ContactDetails from "./ContactDetails";
import PreviousSchoolDetails from "./Previousschooldetails";
import StudentDetails from "./StudentDetails";
import Loading from "./components/Loading";
import QueryBox from "./queryBox";

const Enquiry = () => {
  const navigate = useNavigate();

  // Create refs for each section
  const studentDetailsRef = useRef(null);
  const contactDetailsRef = useRef(null);
  const previousSchoolRef = useRef(null);
  const queryBoxRef = useRef(null);

  const [user, setUser] = useState({
    firstName: "",
    dob: "",
    gender: "",
    dynamicAttributes: [
      {
        name: "How did you know about Velammal? (Eg:TV Ads, Newspaper, etc,.) *",
        value: "",
      },
      {
        name: "Registration Class",
        value: "",
      },
      {
        name: "Branch",
        value: "",
      },
    ],
    contactPersonList: [
      {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        isPrimary: true,
        type: "",
      },
    ],
  });

  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasId = searchParams.has("id"); // Check if the URL contains 'id'

    if (!hasId) return; // Do nothing if 'id' is missing

    const storedData = localStorage.getItem("inquiryData");

    try {
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setUser((prevUser) => ({
          ...prevUser,
          ...parsedData.user,
          contactPersonList: parsedData.user?.contactPersonList.map((contact, index) => ({
            ...prevUser.contactPersonList[index],
            ...contact,
          })) || prevUser.contactPersonList,
          dynamicAttributes: parsedData.user?.dynamicAttributes || prevUser.dynamicAttributes,
        }));
        setPreviousAffiliation(parsedData.previousAffiliation || {});
        setQuery(parsedData.query || "");
      } else {
        navigate("/sign-in");
      }
    } catch {
      navigate("/sign-in");
    }
  }, [location.search, navigate]);

  const [previousAffiliation, setPreviousAffiliation] = useState({
    schoolName: "",
  });
  const [query, setQuery] = useState("");
  const [alert, setAlert] = useState({
    message: "",
    type: "", // 'success' or 'error'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({}); // State to hold validation errors
  const company = useSelector((state) => state.companyReducer.company);
  const companyId = company.primaryId;

  // Function to scroll to a specific error field
  const scrollToError = (errors) => {
    // Define the error fields in the order they appear in the form
    const errorOrder = [
      // Student Details section
      { field: 'firstName', ref: studentDetailsRef },
      { field: 'dob', ref: studentDetailsRef },
      { field: 'gender', ref: studentDetailsRef },
      { field: 'howDidYouKnow', ref: studentDetailsRef },
      { field: 'registrationClass', ref: studentDetailsRef },

      // Contact Details section
      { field: 'contactFirstName', ref: contactDetailsRef },
      { field: 'contactPhoneNumber', ref: contactDetailsRef },
      { field: 'contactEmail', ref: contactDetailsRef },
      { field: 'relationship', ref: contactDetailsRef },

      // Previous School Details section
      { field: 'schoolName', ref: previousSchoolRef },

      // Query section
      { field: 'query', ref: queryBoxRef }
    ];

    // Find the first error based on the defined order
    for (const { field, ref } of errorOrder) {
      if (errors[field] && ref.current) {
        // Scroll to the section containing the error
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        // Focus on the first input field in this section if possible
        const firstInput = ref.current.querySelector('input, select, textarea');
        if (firstInput) {
          setTimeout(() => {
            firstInput.focus();
          }, 500); // Short delay to ensure the scroll completes first
        }

        // Exit after finding the first error
        break;
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate Student Details
    if (!user.firstName) {
      newErrors.firstName = "Student Name is required";
    }
    if (!user.dob) {
      newErrors.dob = "Date of Birth is required";
    }
    if (!user.gender) {
      newErrors.gender = "Gender is required";
    }

    if (!user.dynamicAttributes[0]?.value) {
      newErrors.howDidYouKnow = "How did you know about Velammal? is required";
    }
    if (!user.dynamicAttributes[1]?.value) {
      newErrors.registrationClass = "Registration Class is required";
    }

    // Validate Contact Details
    const primaryContact = user.contactPersonList.find(contact => contact.isPrimary);

    if (!primaryContact?.firstName) {
      newErrors.contactFirstName = "Contact Name is required";
    }
    if (!primaryContact?.phoneNumber) {
      newErrors.contactPhoneNumber = "Mobile Number is required";
    }
    if (!primaryContact?.email) {
      newErrors.contactEmail = "Email Id is required";
    }
    if (!primaryContact?.type) {
      newErrors.relationship = "Relationship is required";
    }

    // Validate Previous School Details
    if (!previousAffiliation.schoolName) {
      newErrors.schoolName = "School Name is required";
    }

    // Validate Query
    if (!query) {
      newErrors.query = "Query is required";
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    if (!isValid) {
      setTimeout(() => {
        scrollToError(newErrors);
      }, 100);
    }
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const updatedContactPersonList = user.contactPersonList.map((contact) => ({
      ...contact,
      phoneNumber: contact.phoneNumber ? `91${contact.phoneNumber.trim()}` : "",
    }));

    const updatedUserDetail = {
      ...user,
      contactPersonList: updatedContactPersonList,
    };

    // Find selected branch by name from dynamicAttributes
    const selectedBranchName = user.dynamicAttributes.find(
      (attr) => attr.name === "Branch"
    )?.value;

    const selectedBranch = company?.branches?.find(
      (branch) => branch.name === selectedBranchName
    );

    const inquiryData = {
      user: updatedUserDetail,
      previousAffiliation,
      query,
      company: selectedBranch
        ? {
          primaryId: selectedBranch.primaryId,
          domain: {
            type: company.domain.type,
          }
        }
        : {
          primaryId: companyId,
          domain: {
            type: company.domain.type,
          }
        },
    };

    try {
      const delay = new Promise((resolve) => setTimeout(resolve, 1500));

      const [result] = await Promise.all([createInquiry(inquiryData), delay]);

      localStorage.setItem("inquiryData", JSON.stringify(result));
      setAlert({ message: "Enquiry sent successfully!", type: "success" });

      navigate("/inquiry-info");
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        "An error occurred. Please try again later.";
      setAlert({ message: errorMessage, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (alert.message) {
      const timer = setTimeout(() => {
        setAlert({ message: "", type: "" });
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [alert.message]);

  useEffect(() => {
    let count = 0;

    if (user.firstName) count++;
    if (user.dob) count++;
    if (user.gender) count++;

    user.dynamicAttributes.forEach(attr => {
      if (attr.value) count++;
    });

    user.contactPersonList.forEach(contact => {
      if (contact.firstName) count++;
      if (contact.email) count++;
      if (contact.phoneNumber) count++;
      if (contact.type) count++;
    });

    if (previousAffiliation.schoolName) count++;
    if (query) count++;

    sessionStorage.setItem('filledFieldsCount', count.toString());
  }, [user, previousAffiliation, query]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem('filledFieldsCount');
    };
  }, []);

  return (
    <>
      {loading && <Loading message="Submitting your enquiry..." />}

      {alert.message && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "15px 20px",
            borderRadius: "8px",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 50,
            transition: "all 0.3s",
            backgroundColor: alert.type === "success" ? "#38a169" : "#e53e3e",
          }}
        >
          {alert.type === "success" ? (
            <AiOutlineCheckCircle style={{ fontSize: "20px" }} />
          ) : (
            <AiOutlineCloseCircle style={{ fontSize: "20px" }} />
          )}
          <span>{alert.message}</span>
        </div>
      )}

      <div
        style={{
          minHeight: "100vh",
          overflow: "hidden",
          background: "linear-gradient(to bottom right, #3182ce, #4c51bf)",
          position: "relative",
        }}
      >
        <div style={{ position: "relative", zIndex: 20 }}>
          <InstitutionHeader />
        </div>

        <br />

        <BackgroundComponent />
        <div
          style={{
            position: "relative",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "auto",
            padding: "40px",
            top: "50px"
          }}
        >
          <div style={{ position: "absolute", top: "30px", left: "20px", zIndex: 50 }}>
            <BackButton />
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: "800px",
              margin: "0 auto",
              padding: "40px",
              background: "rgba(255, 255, 255, 0.9)",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
              overflowY: "auto",
            }}
          >
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "600",
                textAlign: "center",
                marginBottom: "24px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <img src={INSTITUTION_ASSETS.SUCCESS_STORIES_IMAGES.IMAGE_7} alt="feedback icon" style={{ height: "48px", width: "48px" }} />
              <span>Online Enquiry</span>
            </h2>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <div
                ref={studentDetailsRef}
                style={{ border: "1px solid #e2e8f0", padding: "24px", borderRadius: "8px", boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)" }}
              >
                <StudentDetails user={user} setUser={setUser} errors={errors} setErrors={setErrors} />
              </div>
              <div
                ref={contactDetailsRef}
                style={{ border: "1px solid #e2e8f0", padding: "24px", borderRadius: "8px", boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)" }}
              >
                <ContactDetails user={user} setUser={setUser} errors={errors} setErrors={setErrors} />
              </div>
              <div
                ref={previousSchoolRef}
                style={{ border: "1px solid #e2e8f0", padding: "24px", borderRadius: "8px", boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)" }}
              >
                <PreviousSchoolDetails previousAffiliation={previousAffiliation} setPreviousAffiliation={setPreviousAffiliation} errors={errors} setErrors={setErrors} />
              </div>
              <div
                ref={queryBoxRef}
                style={{ border: "1px solid #e2e8f0", padding: "24px", borderRadius: "8px", boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)" }}
              >
                <QueryBox query={query} setQuery={setQuery} errors={errors} setErrors={setErrors} />
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "50%",
                    background: loading ? "#a0aec0" : "linear-gradient(to right, #4299e1, #667eea)",
                    color: "#fff",
                    padding: "12px 0",
                    borderRadius: "8px",
                    fontWeight: "600",
                    textAlign: "center",
                    transition: "background 0.3s",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Submitting..." : "Submit Enquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Enquiry;
